import { windowSize } from '@adornis/base/client/frontend-helpers/window-size.js';
import { isSidebarOpenSubject } from '@adornis/chemistry/elements/components/x-sidebar.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { map } from 'rxjs';
// @ts-expect-error yes i know but it works
import version from '../../../../../version.json';
import { AdminDashboardActions } from '../../admin-dashboard/AdminDashboardPolicy.js';
import { MartinusTemporaryActions } from '../../MartinusTemporaryPolicy.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { type AbacEnabledPage, useAbacEnabledPages } from '../../permissions/client/useAbacEnabledPages.js';
import { EMPLOYEE_ROLES } from '../../permissions/roles.js';
import type { UserWithPermissionFields } from '../../permissions/UserWithPermissionFields.js';
import { isBeamterAbrechnungsgruppe } from '../../users/employees/entities/employee-sections/an-beschaeftigung.js';
import {
  type EmployeeDetailsUser,
  UserEmployeeDetailsActions,
} from '../../users/employees/UserEmployeeDetailsPolicy.js';
import type { Person } from '../../users/entities/person.js';
import { UserListActions } from '../../users/list/UserListPolicy.js';

export enum MainPages {
  KALENDER = 'Kalender',
  WIKI = 'Wiki',
  PERSONEN = 'Personen',
  ADMIN_DASHBOARD = 'Admin Dashboard',
  EIGENE_DATEN = 'Eigene Daten',
  JOB_TICKETS = 'Job-Ticket',
  MEINE_SPENDEN = 'Meine Spenden',
  QUITTUNGEN = 'Quittungen',
  GRUPPEN = 'Gruppen',
  SUS_INTERVIEWS = 'SuS-Anmeldegespräche',
  MICROSOFT = 'Microsoft',
  EINTRITTSFORMULAR = 'Eintrittsformular',
  TEACHING_ASSISTANT = 'Assistent',
  ACCOUNTING = 'Buchhaltung',
}

export const mainPages = {
  kalender: {
    path: '/kalender',
    label: MainPages.KALENDER,
    icon: 'icon-[material-symbols--calendar-month-rounded]',
    attached: (viewedUser: UserWithPermissionFields) => {
      return hasRoles({ user: viewedUser, roles: EMPLOYEE_ROLES });
    },
    permission: UserEmployeeDetailsActions.VIEW_KALENDER,
  },
  wiki: {
    path: '/wiki',
    label: MainPages.WIKI,
    icon: 'icon-[material-symbols--auto-stories-rounded]',
    permission: MartinusTemporaryActions.VIEW_WIKI,
  },
  eintrittsformular: {
    path: '/eintrittsformular',
    label: MainPages.EINTRITTSFORMULAR,
    icon: 'icon-[material-symbols--feed-rounded]',
    attached: (viewedUser: UserWithPermissionFields) => {
      return hasRoles({ user: viewedUser, roles: EMPLOYEE_ROLES });
    },
  },
  person: {
    path: '/personen',
    label: MainPages.PERSONEN,
    icon: 'icon-[material-symbols--groups-3-rounded]',
    permission: UserListActions.USER_LIST,
  },
  adminDashboard: {
    path: '/admin-dashboard',
    label: MainPages.ADMIN_DASHBOARD,
    icon: 'icon-[material-symbols--admin-panel-settings-rounded]',
    permission: AdminDashboardActions.VIEW_ADMIN_DASHBOARD,
  },
  eigeneDaten: {
    path: '/personen/*',
    label: MainPages.EIGENE_DATEN,
    icon: 'icon-[material-symbols--account-circle]',
    onClick: (viewedUser: UserWithPermissionFields) => {
      goTo(`/personen/${viewedUser._id}`);
    },
  },
  ticket: {
    path: '/ticket',
    label: MainPages.JOB_TICKETS,
    icon: 'icon-[material-symbols--confirmation-number-rounded]',
    attached: (viewedUser: EmployeeDetailsUser) => {
      const isEmployee = hasRoles({ user: viewedUser, roles: EMPLOYEE_ROLES });
      const isNotBeamter = !isBeamterAbrechnungsgruppe(
        viewedUser.mitarbeiterDaten?.anBeschaeftigung?.abrechnungsgruppe,
      );

      return isEmployee && isNotBeamter;
    },
  },
  spenden: {
    path: '/spenden',
    label: MainPages.MEINE_SPENDEN,
    icon: 'icon-[material-symbols--volunteer-activism-rounded]',
  },
  downloads: {
    path: '/quittungen',
    label: MainPages.QUITTUNGEN,
    icon: 'icon-[material-symbols--download-rounded]',
  },
  gruppen: {
    path: '/groups',
    label: MainPages.GRUPPEN,
    icon: 'icon-[material-symbols--group]',
    permission: MartinusTemporaryActions.VIEW_GROUPS,
  },
  teachingAssistant: {
    path: '/teaching-assistant',
    label: MainPages.TEACHING_ASSISTANT,
    icon: 'icon-[material-symbols--network-intel-node-rounded]',
    permission: MartinusTemporaryActions.VIEW_TEACHING_ASSISTANT,
  },
  interviewAdministration: {
    path: '/interview-administration',
    label: MainPages.SUS_INTERVIEWS,
    icon: 'icon-[material-symbols--family-restroom-rounded]',
    permission: MartinusTemporaryActions.VIEW_INTERVIEW_ADMINISTRATION,
  },
  accounting: {
    path: '/accounting',
    label: MainPages.ACCOUNTING,
    icon: 'icon-[material-symbols--account-balance]',
    permission: MartinusTemporaryActions.VIEW_ACCOUNTING,
  },
} as const satisfies Record<
  string,
  AbacEnabledPage<EmployeeDetailsUser> & { icon: string; onClick?: (viewedPerson: EmployeeDetailsUser) => void }
>;

const isActiveRoute = (currentPath: string, pagePath?: string) => {
  if (!pagePath) return false;
  return currentPath === pagePath || currentPath.startsWith(`${pagePath}/`);
};

type SidebarPage = { path?: string; label: string; icon: string; onClick?: (user: EmployeeDetailsUser) => void };

const renderSidebarLink = (page: SidebarPage, currentPath: string, narrow: boolean, user: EmployeeDetailsUser) => {
  const isActive = isActiveRoute(currentPath, page.path);
  // Fixed size icon: w-6 h-6 (24px) with centered content
  const icon = html`<span class="${page.icon} inline-flex items-center justify-center w-6 h-6 shrink-0"></span>`;
  const label = narrow ? nothing : html`<span class="truncate">${page.label}</span>`;

  // Fixed dimensions: narrow mode uses w-10 h-10 (40px) square buttons, wide uses full width
  const baseClasses = narrow
    ? 'flex items-center justify-center w-10 h-10 shrink-0 text-sm font-medium transition-colors duration-200 rounded-lg'
    : 'group flex items-center w-full min-w-0 min-h-11 text-sm font-medium transition-colors duration-200 rounded-lg py-2 px-3 gap-3';
  const stateClasses = isActive
    ? 'text-primary bg-primary/10'
    : 'text-base-content hover:bg-base-200 hover:text-primary';

  const activeIndicator =
    isActive && !narrow
      ? html`<span class="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary"></span>`
      : nothing;

  if (page.path && !page.onClick) {
    return html`
      <a class="${baseClasses} ${stateClasses} relative" href="${page.path}" title=${narrow ? page.label : ''}>
        ${activeIndicator} ${icon} ${label}
      </a>
    `;
  }

  return html`
    <button
      class="${baseClasses} ${stateClasses} cursor-pointer relative"
      type="button"
      title=${narrow ? page.label : ''}
      @click=${() => page.onClick?.(user)}
    >
      ${activeIndicator} ${icon} ${label}
    </button>
  `;
};

// Mobile breakpoint: devices <= bigPhone are considered mobile
const isMobile = (width: number) => width <= 480; // bigPhone breakpoint

let desktopDefaultInitialized = false;

export const Sidebar = stateful(() => {
  const isOpen = useObservable(() => isSidebarOpenSubject, [isSidebarOpenSubject.value]);
  const windowWidth = useObservable(() => windowSize.pipe(map(s => s.width)), [window.innerWidth]);
  const currentPath = routingStateSignal.value.path;
  const mobile = isMobile(windowWidth ?? window.innerWidth);

  // On desktop, sidebar should be open by default
  if (!desktopDefaultInitialized && !mobile) {
    desktopDefaultInitialized = true;
    isSidebarOpenSubject.next(true);
  }
  const user = useObservable(() => currentUser<Person>(), []);
  const displayName = user?.displayName ?? '';

  const allowedPages = useAbacEnabledPages<EmployeeDetailsUser>({
    pages: Object.values(mainPages),
    viewedUser: user,
  });

  // Map allowed pages back to mainPages to preserve icon property
  const pagesWithIcons = (allowedPages ?? [])
    .map(allowed => Object.values(mainPages).find(p => p.label === allowed.label))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  const handleLogout = async () => {
    await logout();
    goTo('/');
  };

  // Mobile: hidden when sidebar is closed, desktop: narrow when closed, wide when open
  const hidden = mobile && !isOpen;
  const narrow = mobile ? false : !isOpen;

  if (hidden || !user) {
    return nothing;
  }

  return html`
    <div
      class="flex h-full flex-col border-r border-base-300 shrink-0 ${
        mobile
          ? 'fixed left-0 top-0 z-50 w-full bg-base-100 transition-transform duration-200'
          : 'bg-base-100/80 backdrop-blur-md backdrop-saturate-150 transition-[width] duration-200'
      } ${mobile ? 'translate-x-0' : narrow ? 'w-16' : 'w-64'}"
    >
      ${
        mobile
          ? html`
              <div class="flex items-center justify-end p-4">
                <button class="btn btn-ghost btn-circle btn-sm" @click=${() => isSidebarOpenSubject.next(false)}>
                  <span class="icon-[material-symbols--close-rounded] inline-block h-5 w-5"></span>
                </button>
              </div>
            `
          : nothing
      }
      <div class="flex min-h-16 items-center justify-center px-2 py-2">
        <button
          class="flex items-center transition-all duration-200 justify-center rounded-2xl bg-martinus-secondary p-3 shadow-sm hover:scale-105 cursor-pointer"
          type="button"
          @click=${() => goTo(`/`)}
        >
          <img
            src="${require('../../logos/sg-sankt-martinus.png')}"
            alt="SG Sankt Martinus"
            class="${narrow ? 'h-6' : 'h-12'} w-auto object-contain"
          />
        </button>
      </div>

      <nav class="flex-1 overflow-y-auto px-2 py-3">
        <div class="flex flex-col gap-1 justify-center items-center">
          ${repeat(
            pagesWithIcons,
            page => page.label,
            page => renderSidebarLink(page, currentPath, narrow, user),
          )}
        </div>
      </nav>

      <div class="border-t border-base-300 px-2 py-3">
        ${
          user && !narrow
            ? html`
                <div
                  class="mb-2 truncate px-3 text-sm text-base-content/70 justify-center text-center"
                  title="${displayName}"
                >
                  ${displayName}
                </div>
              `
            : nothing
        }
        <button
          class="btn btn-outline btn-error w-full ${narrow ? 'btn-square px-0' : 'justify-start gap-2'}"
          type="button"
          title="Ausloggen"
          @click=${handleLogout}
        >
          <span class="icon-[material-symbols--logout-rounded] inline-block h-5 w-5 shrink-0"></span>
          ${narrow ? nothing : html`<span>Ausloggen</span>`}
        </button>

        ${
          !narrow
            ? html`
                <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 px-3 justify-center">
                  <span class="text-sm text-base-content/70">
                    <b>Version:</b> ${version.release} (${version.commit})
                  </span>
                </div>
                <div class="mt-2 flex flex-wrap items-center gap-1 px-1 justify-between">
                  <a class="text-sm text-base-content/70 hover:text-primary" href="/datenschutz"> Datenschutz </a>
                  <a class="text-sm text-base-content/70 hover:text-primary" href="/impressum"> Impressum </a>
                </div>
              `
            : nothing
        }
      </div>
    </div>
  `;
});
